/**
 * Owner: Masa Bayu
 * File: library-security.ts
 * Date:Time: 2026-09-15 23:21
 * Description: T4 library security — E1–E7; share ≠ entitlement/content/Reader.
 */
export const QXREAD_T4_E1_PROHIBITED = [
    'QXRead → invent LibraryItem without Entitlement',
    'QXRead → treat Wishlist as Entitlement',
    'QXRead → execute QXReader from Library UI',
    'QXRead → mutate QXP from Library',
    'QXRead → charge Payment from Library',
    'QXRead → mint Entitlement from Library',
    'QXRead → show publication content bytes in Library',
    'QXRead → unlock Offline without active Entitlement',
];
export const QXREAD_T4_E1_SECURITY = Object.freeze({
    libraryRequiresEntitlement: true,
    wishlistIsNotEntitlement: true,
    libraryDoesNotExecuteReader: true,
    libraryDoesNotMutateQxp: true,
    libraryDoesNotChargePayment: true,
    libraryDoesNotMintEntitlement: true,
    libraryDoesNotShowContentBytes: true,
    offlineRequiresActiveEntitlement: true,
    readerLaunchIsBoundaryOnly: true,
    accountScopedProjection: true,
});
export function isT4E1ProhibitedOperation(operation) {
    return QXREAD_T4_E1_PROHIBITED.includes(operation);
}
export function assertT4E1SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E1_SECURITY)) {
        const expected = QXREAD_T4_E1_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E1 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E1_SECURITY;
}
export const QXREAD_T4_E2_PROHIBITED = [
    'QXRead → grant access from Library UI state',
    'QXRead → treat progress percent as Entitlement',
    'QXRead → execute Flip/runtime from resume intent',
    'QXRead → mutate QXP version from Library version awareness',
    'QXRead → invent ownership via filter/search results',
];
export const QXREAD_T4_E2_SECURITY = Object.freeze({
    uiStateDoesNotGrantAccess: true,
    progressIsNotEntitlement: true,
    resumeIntentDoesNotExecuteReader: true,
    versionAwarenessDoesNotMutateQxp: true,
    filterSearchDoesNotInventOwnership: true,
    entitlementRemainsAuthoritative: true,
});
export function isT4E2ProhibitedOperation(operation) {
    return QXREAD_T4_E2_PROHIBITED.includes(operation);
}
export function assertT4E2SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E2_SECURITY)) {
        const expected = QXREAD_T4_E2_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E2 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E2_SECURITY;
}
export const QXREAD_T4_E3_PROHIBITED = [
    'QXRead → create Entitlement from Library sync',
    'QXRead → grant access from offline library state',
    'QXRead → grant access from continuity snapshot',
    'QXRead → execute Flip/runtime from sync reconcile',
    'QXRead → mint ownership via sync queue replay',
];
export const QXREAD_T4_E3_SECURITY = Object.freeze({
    entitlementRemainsAuthoritative: true,
    syncCannotCreateEntitlement: true,
    offlineStateCannotGrantAccess: true,
    continuityCannotGrantAccess: true,
    syncCannotExecuteReader: true,
    queueReplayDoesNotMintOwnership: true,
});
export function isT4E3ProhibitedOperation(operation) {
    return QXREAD_T4_E3_PROHIBITED.includes(operation);
}
export function assertT4E3SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E3_SECURITY)) {
        const expected = QXREAD_T4_E3_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E3 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E3_SECURITY;
}
export const QXREAD_T4_E4_PROHIBITED = [
    'QXRead → create Entitlement from Library access resilience',
    'QXRead → extend Entitlement expiry from Library',
    'QXRead → restore access without authoritative Entitlement',
    'QXRead → grant access after session loss via offline flag',
    'QXRead → execute Flip/runtime from access revalidation',
];
export const QXREAD_T4_E4_SECURITY = Object.freeze({
    libraryProjectsAccessOnly: true,
    libraryCannotCreateEntitlement: true,
    libraryCannotExtendEntitlement: true,
    revalidationDoesNotMintAccess: true,
    sessionLossDeniesAccess: true,
    offlineStaleRequiresRevalidation: true,
    entitlementRemainsAuthoritative: true,
});
export function isT4E4ProhibitedOperation(operation) {
    return QXREAD_T4_E4_PROHIBITED.includes(operation);
}
export function assertT4E4SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E4_SECURITY)) {
        const expected = QXREAD_T4_E4_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E4 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E4_SECURITY;
}
export const QXREAD_T4_E5_PROHIBITED = [
    'QXRead → execute QXReader from Library launch contract',
    'QXRead → execute Flip/runtime from deep-link resolution',
    'QXRead → render pages from Library launch intent',
    'QXRead → start media runtime from Library launch intent',
    'QXRead → mint Entitlement from launch/deep-link contract',
];
export const QXREAD_T4_E5_SECURITY = Object.freeze({
    launchIntentDoesNotExecuteReader: true,
    deepLinkDoesNotExecuteReader: true,
    launchContractIsDeterministic: true,
    launchRequiresValidProjection: true,
    expiredLaunchRequiresRevalidation: true,
    entitlementRemainsAuthoritative: true,
});
export function isT4E5ProhibitedOperation(operation) {
    return QXREAD_T4_E5_PROHIBITED.includes(operation);
}
export function assertT4E5SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E5_SECURITY)) {
        const expected = QXREAD_T4_E5_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E5 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E5_SECURITY;
}
export const QXREAD_T4_E6_PROHIBITED = [
    'QXRead → create Entitlement from Library collection',
    'QXRead → extend Entitlement from Library favorite',
    'QXRead → treat organization as access authority',
    'QXRead → execute QXReader from personalized Library view',
    'QXRead → invent ownership via collection membership',
];
export const QXREAD_T4_E6_SECURITY = Object.freeze({
    organizationDoesNotCreateEntitlement: true,
    organizationDoesNotExtendEntitlement: true,
    favoriteIsNotEntitlement: true,
    collectionIsNotEntitlement: true,
    personalizedViewDoesNotExecuteReader: true,
    entitlementRemainsAuthoritative: true,
});
export function isT4E6ProhibitedOperation(operation) {
    return QXREAD_T4_E6_PROHIBITED.includes(operation);
}
export function assertT4E6SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E6_SECURITY)) {
        const expected = QXREAD_T4_E6_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E6 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E6_SECURITY;
}
export const QXREAD_T4_E7_PROHIBITED = [
    'QXRead → transfer Entitlement via Library share',
    'QXRead → create Entitlement via Library share',
    'QXRead → expose reading position via Library share',
    'QXRead → expose protected content via Library share',
    'QXRead → execute QXReader from Library share handoff',
];
export const QXREAD_T4_E7_SECURITY = Object.freeze({
    shareDoesNotTransferEntitlement: true,
    shareDoesNotCreateEntitlement: true,
    shareDoesNotExposeReadingPosition: true,
    shareDoesNotExposeProtectedContent: true,
    handoffRequiresIndependentEntitlement: true,
    shareDoesNotExecuteReader: true,
    entitlementRemainsAuthoritative: true,
});
export function isT4E7ProhibitedOperation(operation) {
    return QXREAD_T4_E7_PROHIBITED.includes(operation);
}
export function assertT4E7SecurityIntact(overrides = {}) {
    for (const key of Object.keys(QXREAD_T4_E7_SECURITY)) {
        const expected = QXREAD_T4_E7_SECURITY[key];
        if (Object.prototype.hasOwnProperty.call(overrides, key) && overrides[key] !== expected) {
            throw new Error(`QXRead T4-E7 security violated: ${key}`);
        }
    }
    return QXREAD_T4_E7_SECURITY;
}
//# sourceMappingURL=library-security.js.map